import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { hideModal as hideModalAction } from 'actions/modalActions';
import { usePressEscape } from 'hooks';
import { Button, Layout, Typography, Modal, Alert } from '@hub-mono/ui';
import { useModalOffset } from '../../../../hooks/useModalOffset';
import styled from 'styled-components';

export const REPEAT_MODAL_TYPES = {
    MILESTONE: {
        title: 'Delete milestone',
        description: () =>
            'This is a repeating milestone. Choose if you would like to just delete this milestone, All milestones or all future milestones.',
    },
    PHASE: {
        title: 'Delete phase',
        description: () =>
            'This is a repeating phase. Choose if you would like to just delete this phase, All phases or all future phases.',
    },
    BOOKING: {
        title: 'Update booking',
        description: isMultiMove =>
            isMultiMove
                ? 'You are trying to update one or more repeated bookings. Choose if you would like to just update selected booking or all bookings. (incl. repeated)'
                : 'This is a repeating booking. Choose if you would like to just update this booking or all bookings.',
    },
};

const TitleContainer = styled(Layout)`
    min-height: 48px;
`;

const EditRepeatModal = props => {
    const { type, actions, bookings } = props;

    const isMultiMove = Array.isArray(bookings);
    const offsetClassNames = useModalOffset();

    const dispatch = useDispatch();
    const { onCancel, updateAll: onUpdateAll, updateOne: onUpdateOne } = actions;

    const hideModal = useCallback(() => {
        dispatch(hideModalAction());
    }, [dispatch]);

    const cancel = useCallback(() => {
        onCancel && onCancel();
        hideModal();
    }, [hideModal, onCancel]);

    usePressEscape(cancel);

    const updateAll = useCallback(() => {
        onUpdateAll();
        hideModal();
    }, [hideModal, onUpdateAll]);

    const updateOne = useCallback(() => {
        onUpdateOne();
        hideModal();
    }, [hideModal, onUpdateOne]);

    const title = (
        <TitleContainer vAlign="center">
            <Typography variant="label" size="xl">
                {REPEAT_MODAL_TYPES[type].title}
            </Typography>
        </TitleContainer>
    );

    const alertText = isMultiMove
        ? 'Warning, If you update just selected bookings, they will be updated to the new position and removed from the repeat cycle. If you update all, all bookings will be updated to the new position.'
        : 'Warning, If you update just this booking, it will be updated to the new position and removed from the repeat cycle. If you update all, all bookings will be updated to the new position.';

    const content = (
        <Layout stack gap="--Spacing-200">
            <Typography>{REPEAT_MODAL_TYPES[type].description(isMultiMove)}</Typography>
            {'BOOKING' === type && <Alert variant="error">{alertText}</Alert>}
        </Layout>
    );

    const actionsJSX = (
        <Layout hAlign="flex-end" gap="--Spacing-150">
            <Button onClick={cancel} dataCy="button--cancel" variant="text">
                Cancel
            </Button>
            <Button onClick={updateAll} variant="outlined" dataCy="button--update-all">
                Update all
            </Button>
            <Button onClick={updateOne} dataCy="button--update-this">
                {isMultiMove ? 'Update selected' : 'Update this'}
            </Button>
        </Layout>
    );

    return (
        <Modal
            topDivider
            bottomDivider={false}
            className={offsetClassNames}
            level="high"
            variant="dialog"
            onClose={cancel}
            title={title}
            content={content}
            actions={actionsJSX}
            fullWidth="sm"
        />
    );
};

EditRepeatModal.propTypes = {
    type: PropTypes.oneOf(Object.keys(REPEAT_MODAL_TYPES)).isRequired,
    actions: PropTypes.shape({
        updateAll: PropTypes.func.isRequired,
        updateOne: PropTypes.func.isRequired,
        onCancel: PropTypes.func,
    }),
};

export default EditRepeatModal;
